/*
 * One page's target keywords against the page as Google is served it: is each phrase in the
 * title, the H1, the meta description, an H2, the body text, and does Google show the page for
 * exactly that query. Used on the Categories tab and on a page's own record.
 *
 * The checks come from the backend's daily read of the live page (SeoKeywordMatcher), so a page
 * synced before the checks existed shows "not checked yet" until the next sync.
 */
import CRMStatusChip from '@components/Common/CRMStatusChip';
import EmptyState from '@components/Common/EmptyState';
import MetricTable, { type MetricColumn } from '@components/Common/MetricTable';
import React from 'react';

import YesNo from './YesNo';
import {
  type KeywordRow,
  type SeoKeyword,
  type SeoKeywordCheck,
  fmtCount,
  fmtPosition,
  keywordPlacement,
  keywordRows,
} from './seoData';

const PLACEMENT = {
  strong: { status: 'success', label: 'In title or headings' },
  body: { status: 'pending', label: 'Body only' },
  missing: { status: 'failed', label: 'Not on page' },
} as const;

const COLUMNS: MetricColumn<KeywordRow>[] = [
  {
    key: 'keyword',
    header: 'Keyword',
    variant: 'name',
    render: (k) => (
      <span className="seo-kw__name">
        {k.keyword}
        {k.primary && <CRMStatusChip status="active" label="Primary" />}
      </span>
    ),
  },
  {
    key: 'on',
    header: 'On the page',
    align: 'left',
    tooltip:
      'In title or headings: the title, H1, meta description or an H2. Body only: just the page text.',
    render: (k) => {
      const p = PLACEMENT[keywordPlacement(k)];
      return <CRMStatusChip status={p.status} label={p.label} />;
    },
  },
  { key: 'title', header: 'Title', align: 'center', render: (k) => <YesNo on={k.inTitle} /> },
  { key: 'h1', header: 'H1', align: 'center', render: (k) => <YesNo on={k.inH1} /> },
  {
    key: 'meta',
    header: 'Meta',
    align: 'center',
    tooltip: 'The meta description',
    render: (k) => <YesNo on={k.inDescription} />,
  },
  { key: 'h2', header: 'H2', align: 'center', render: (k) => <YesNo on={k.inH2} /> },
  { key: 'body', header: 'Body', align: 'center', render: (k) => <YesNo on={k.inBody} /> },
  {
    key: 'position',
    header: 'Google position',
    tooltip:
      'Average position for exactly this query on this page over 28 days. A dash: Google did not show the page for it.',
    render: (k) => (k.position === null ? '-' : fmtPosition(k.position)),
  },
  {
    key: 'clicks',
    header: 'Clicks',
    variant: 'strong',
    tooltip: 'Clicks from exactly this query in the last 28 days',
    render: (k) => (k.clicks === null ? '-' : fmtCount(k.clicks)),
  },
];

/** "4 of 6 on the page", for a card's meta line. */
export const coverageLabel = (checks: SeoKeywordCheck[] | undefined): string | undefined => {
  if (!checks?.length) return undefined;
  const on = checks.filter((c) => c.onPage).length;
  return `${on} of ${checks.length} on the page`;
};

const SeoKeywordChecks: React.FC<{
  checks: SeoKeywordCheck[] | undefined;
  queries: SeoKeyword[];
  hasKeywords: boolean;
}> = ({ checks, queries, hasKeywords }) => {
  if (!hasKeywords) {
    return (
      <EmptyState
        compact
        title="No target keywords"
        body="The site sets no primary or secondary keyword for this page."
      />
    );
  }
  if (!checks?.length) {
    return (
      <EmptyState
        compact
        title="Not checked yet"
        body="The keywords are checked against the live page on each sync. Run Sync now on the SEO page to check them today."
      />
    );
  }
  return (
    <MetricTable
      columns={COLUMNS}
      rows={keywordRows(checks, queries)}
      getRowKey={(r) => r.keyword}
      pinName
    />
  );
};

export default SeoKeywordChecks;
